(() => {
  'use strict';

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const config = window.SOIERIE || {};
  const strings = config.strings || {};
  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const t = (key, fallback) => strings[key] || fallback;
  const interpolate = (template, values = {}) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{{ ${key} }}`, String(value)).replaceAll(`{{${key}}}`, String(value)), template);
  const escapeHTML = (value) => String(value == null ? '' : value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    const value = Number(number || 0) / 100;
    if (!Number.isFinite(value)) return '0';
    const parts = value.toFixed(precision).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
  };
  const formatMoney = (cents) => {
    const format = config.moneyFormat || '${{amount}}';
    const replacements = {
      amount: formatWithDelimiters(cents, 2, ',', '.'),
      amount_no_decimals: formatWithDelimiters(cents, 0, ',', '.'),
      amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ','),
      amount_with_apostrophe_separator: formatWithDelimiters(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: formatWithDelimiters(cents, 0, ' ', '.')
    };
    return Object.entries(replacements).reduce((output, [key, value]) => output.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), value), format);
  };

  const setBodyLock = (locked) => document.body.classList.toggle('is-locked', locked);
  const getFocusable = (container) => qsa('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', container).filter((el) => !el.hidden);

  class OverlayController {
    constructor() {
      this.scrim = qs('[data-overlay-scrim]');
      this.active = null;
      this.returnFocus = null;
      this.onKeydown = this.onKeydown.bind(this);
      if (this.scrim) this.scrim.addEventListener('click', () => this.close());
    }

    open(element, trigger) {
      if (!element) return;
      if (this.active && this.active !== element) this.close(false);
      this.active = element;
      this.returnFocus = trigger || document.activeElement;
      element.classList.add('is-visible');
      element.setAttribute('aria-hidden', 'false');
      if (this.scrim) {
        this.scrim.hidden = false;
        requestAnimationFrame(() => this.scrim.classList.add('is-visible'));
      }
      setBodyLock(true);
      document.addEventListener('keydown', this.onKeydown);
      const focusable = getFocusable(element);
      if (focusable[0]) focusable[0].focus();
    }

    close(restoreFocus = true) {
      if (!this.active) return;
      this.active.classList.remove('is-visible');
      this.active.setAttribute('aria-hidden', 'true');
      if (this.scrim) {
        this.scrim.classList.remove('is-visible');
        window.setTimeout(() => { if (!this.active) this.scrim.hidden = true; }, 260);
      }
      const focusTarget = this.returnFocus;
      this.active = null;
      this.returnFocus = null;
      setBodyLock(false);
      document.removeEventListener('keydown', this.onKeydown);
      if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.active) return;
      const focusable = getFocusable(this.active);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const overlays = new OverlayController();

  class CartDrawer {
    constructor() {
      this.drawer = qs('[data-cart-drawer]');
      this.body = qs('[data-cart-drawer-body]');
      this.footer = qs('[data-cart-drawer-footer]');
      this.counts = qsa('[data-cart-count]');
      this.bind();
    }

    bind() {
      document.addEventListener('click', (event) => {
        const open = event.target.closest('[data-open-cart]');
        if (open && window.SOIERIE && window.SOIERIE.cartDrawerEnabled) {
          event.preventDefault();
          this.open(open);
        }
        if (event.target.closest('[data-close-cart]')) overlays.close();
        const change = event.target.closest('[data-cart-change]');
        if (change) {
          event.preventDefault();
          this.change(change.dataset.key, Number(change.dataset.quantity));
        }
      });

      document.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-ajax-product-form]');
        if (!form || !(window.SOIERIE && window.SOIERIE.cartDrawerEnabled)) return;
        event.preventDefault();
        this.add(form);
      });
    }

    async request(path, options = {}) {
      const response = await fetch(root + path, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', ...(options.headers || {}) },
        ...options
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || t('cartError', 'Unable to update your cart.'));
      return data;
    }

    async getCart() {
      return this.request('cart.js');
    }

    async add(form) {
      const submit = qs('[type="submit"]', form);
      const original = submit ? submit.textContent : '';
      if (submit) {
        submit.disabled = true;
        submit.textContent = t('adding', 'Adding…');
      }
      try {
        await this.request('cart/add.js', { method: 'POST', body: new FormData(form) });
        const cart = await this.getCart();
        this.render(cart);
        overlays.open(this.drawer, submit || form);
        document.dispatchEvent(new CustomEvent('soierie:cart-updated', { detail: cart }));
      } catch (error) {
        this.showError(error.message);
      } finally {
        if (submit) {
          submit.disabled = false;
          submit.textContent = original;
        }
      }
    }

    async change(key, quantity) {
      try {
        const cart = await this.request('cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
        });
        this.render(cart);
        document.dispatchEvent(new CustomEvent('soierie:cart-updated', { detail: cart }));
      } catch (error) {
        this.showError(error.message);
      }
    }

    async open(trigger) {
      if (!this.drawer) return;
      overlays.open(this.drawer, trigger);
      try {
        this.render(await this.getCart());
      } catch (error) {
        this.showError(error.message);
      }
    }

    showError(message) {
      if (!this.body) return;
      const error = document.createElement('p');
      error.className = 'form-message form-message--error';
      error.textContent = message;
      this.body.prepend(error);
      if (this.drawer) overlays.open(this.drawer);
    }

    renderShipping(cart) {
      const threshold = Number((window.SOIERIE && window.SOIERIE.freeShippingThreshold) || 0);
      if (!threshold) return '';
      const remaining = Math.max(0, threshold - cart.total_price);
      const progress = Math.min(100, (cart.total_price / threshold) * 100);
      const text = remaining > 0
        ? interpolate(t('shippingRemaining', 'You are {{ amount }} away from complimentary shipping'), { amount: formatMoney(remaining) })
        : t('shippingUnlocked', 'Complimentary shipping unlocked');
      return `<div class="shipping-progress"><div class="shipping-progress__text">${text}</div><div class="shipping-progress__track"><div class="shipping-progress__fill" style="--progress:${progress}%"></div></div></div>`;
    }

    render(cart) {
      this.counts.forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });
      qsa('[data-cart-page-count]').forEach((count) => { count.textContent = cart.item_count; });
      if (!this.body || !this.footer) return;
      if (!cart.items.length) {
        this.body.innerHTML = `<div class="drawer-empty"><p>${escapeHTML(t('cartEmpty', 'Your selection is currently empty.'))}</p><a class="text-link" href="${root}collections/all">${escapeHTML(t('continueShopping', 'Continue shopping'))}</a></div>`;
        this.footer.innerHTML = '';
        return;
      }
      this.body.innerHTML = cart.items.map((item) => {
        const image = item.featured_image ? item.featured_image.url : '';
        const imageAlt = item.featured_image ? item.featured_image.alt || item.product_title : item.product_title;
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        return `<article class="cart-line">
          <a href="${escapeHTML(item.url)}"><img src="${escapeHTML(image)}" alt="${escapeHTML(imageAlt)}" width="184" height="232" loading="lazy"></a>
          <div><a class="cart-line__title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a><div class="cart-line__variant">${escapeHTML(variant)}</div>
            <div class="cart-quantity" aria-label="${escapeHTML(t('quantity', 'Quantity'))}">
              <button type="button" data-cart-change data-key="${escapeHTML(item.key)}" data-quantity="${item.quantity - 1}" aria-label="${escapeHTML(t('decreaseQuantity', 'Decrease quantity'))}">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-change data-key="${escapeHTML(item.key)}" data-quantity="${item.quantity + 1}" aria-label="${escapeHTML(t('increaseQuantity', 'Increase quantity'))}">+</button>
            </div>
          </div>
          <div class="cart-line__price">${formatMoney(item.final_line_price)}<button class="cart-line__remove" type="button" data-cart-change data-key="${escapeHTML(item.key)}" data-quantity="0">${escapeHTML(t('remove', 'Remove'))}</button></div>
        </article>`;
      }).join('');
      this.footer.innerHTML = `${this.renderShipping(cart)}<div class="drawer-total"><span>${escapeHTML(t('subtotal', 'Subtotal'))}</span><span>${formatMoney(cart.total_price)}</span></div><a class="button button--full" href="${root}checkout">${escapeHTML(t('checkout', 'Secure checkout'))}</a><p class="text-muted drawer-tax-note">${escapeHTML(t('taxesShipping', 'Taxes, duties, and shipping are calculated at checkout.'))}</p>`;
    }
  }

  const cartDrawer = new CartDrawer();

  function initMobileMenu() {
    const menu = qs('[data-mobile-menu]');
    const open = qs('[data-mobile-menu-toggle]');
    const close = qs('[data-mobile-menu-close]');
    if (!menu || !open) return;
    const show = () => {
      menu.hidden = false;
      open.setAttribute('aria-expanded', 'true');
      setBodyLock(true);
      if (close) close.focus();
    };
    const hide = () => {
      menu.hidden = true;
      open.setAttribute('aria-expanded', 'false');
      setBodyLock(false);
      open.focus();
    };
    open.addEventListener('click', show);
    if (close) close.addEventListener('click', hide);
    menu.addEventListener('keydown', (event) => { if (event.key === 'Escape') hide(); });
  }

  function initQuantitySelectors() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-action]');
      if (!button) return;
      const selector = button.closest('[data-quantity-selector]');
      const input = qs('input[type="number"]', selector);
      if (!input) return;
      const step = Number(input.step || 1);
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const next = button.dataset.quantityAction === 'increase' ? Number(input.value || min) + step : Number(input.value || min) - step;
      input.value = Math.min(max, Math.max(min, next));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function initCountdowns() {
    qsa('[data-countdown-end]').forEach((element) => {
      const end = Date.parse(element.dataset.countdownEnd || '');
      if (!Number.isFinite(end)) {
        element.hidden = true;
        return;
      }
      const expiredText = element.dataset.countdownExpired || 'Offer ended';
      const fullClock = element.matches('.countdown');
      const update = () => {
        const distance = end - Date.now();
        if (distance <= 0) {
          if (fullClock) element.innerHTML = `<p>${expiredText}</p>`;
          else element.textContent = expiredText;
          return false;
        }
        const totalSeconds = Math.floor(distance / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        if (fullClock) {
          const values = { days, hours, minutes, seconds };
          Object.entries(values).forEach(([unit, value]) => {
            const target = qs(`[data-countdown-${unit}]`, element);
            if (target) target.textContent = String(value).padStart(2, '0');
          });
        } else {
          element.textContent = days > 0 ? `${days}d ${String(hours).padStart(2, '0')}h` : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        }
        return true;
      };
      if (update()) window.setInterval(update, 1000);
    });
  }

  function initProductSections() {
    qsa('[data-product-section]').forEach((section) => {
      const form = qs('[data-main-product-form]', section);
      const select = qs('[data-variant-select]', section);
      const json = qs('[data-product-variants]', section);
      if (!form || !select || !json) return;
      let variants = [];
      try { variants = JSON.parse(json.textContent); } catch (_) { return; }
      const button = qs('[data-product-submit]', form);
      const buttonText = qs('[data-product-submit-text]', button || form);
      const stickyButton = qs('[data-sticky-submit]', section);
      const stickyPrice = qs('[data-sticky-price]', section);
      const price = qs('[data-product-price]', section);
      const inventory = qs('[data-product-inventory]', section);
      const saleBadge = qs('[data-product-sale-badge]', section);
      const skuValue = qs('[data-product-sku]', section);
      const choices = qsa('[data-variant-choice]', section);
      const threshold = Number(section.dataset.lowStockThreshold || 5);
      const gallery = qs('[data-product-gallery]', section);
      const galleryPanels = gallery ? qsa('[data-gallery-panel]', gallery) : [];
      const galleryThumbs = gallery ? qsa('[data-gallery-thumb]', gallery) : [];
      const galleryCurrent = gallery ? qs('[data-gallery-current]', gallery) : null;

      const activateGalleryMedia = (mediaIdOrIndex) => {
        if (!galleryPanels.length) return;
        let targetIndex = galleryPanels.findIndex((panel) => String(panel.dataset.mediaId) === String(mediaIdOrIndex));
        if (targetIndex < 0 && Number.isFinite(Number(mediaIdOrIndex))) targetIndex = Number(mediaIdOrIndex);
        if (targetIndex < 0 || targetIndex >= galleryPanels.length) return;
        galleryPanels.forEach((panel, index) => {
          const active = index === targetIndex;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
          if (!active) {
            const video = qs('video', panel);
            if (video && !video.paused) video.pause();
          }
        });
        galleryThumbs.forEach((thumb, index) => {
          const active = index === targetIndex;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-pressed', String(active));
        });
        if (galleryCurrent) galleryCurrent.textContent = String(targetIndex + 1);
      };

      galleryThumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => activateGalleryMedia(thumb.dataset.galleryMediaId || thumb.dataset.galleryThumb));
      });

      const update = ({ focusMedia = false } = {}) => {
        const variant = variants.find((item) => String(item.id) === String(select.value));
        if (!variant) return;
        if (price) price.innerHTML = variant.price_html;
        if (stickyPrice) stickyPrice.innerHTML = variant.price_html;
        if (skuValue && variant.sku) skuValue.textContent = variant.sku;
        if (saleBadge) {
          const discount = Number(variant.discount_percent || 0);
          saleBadge.hidden = discount <= 0;
          if (discount > 0) saleBadge.textContent = `-${discount}% ${t('off', 'off')}`;
        }
        const available = Boolean(variant.available);
        if (button) button.disabled = !available;
        if (stickyButton) stickyButton.disabled = !available;
        const label = available ? t('addToCart', 'Add to cart') : t('soldOut', 'Sold out');
        if (buttonText) buttonText.textContent = label;
        if (stickyButton) stickyButton.textContent = label;
        choices.forEach((choice) => {
          const selected = String(choice.dataset.variantId) === String(variant.id);
          choice.classList.toggle('is-selected', selected);
          choice.setAttribute('aria-pressed', String(selected));
        });
        if (inventory) {
          let status = '';
          let statusClass = 'inventory-status';
          if (!available) {
            status = t('soldOut', 'Sold out');
            statusClass += ' inventory-status--out';
          } else if (variant.inventory_management && variant.inventory_policy === 'deny' && variant.inventory_quantity <= threshold) {
            status = interpolate(t('lowStock', 'Only {{ count }} left'), { count: Math.max(0, variant.inventory_quantity) });
            statusClass += ' inventory-status--low';
          } else if (variant.inventory_management && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') {
            status = t('preorder', 'Available to order');
          } else {
            status = t('inStock', 'In stock');
          }
          inventory.className = statusClass;
          const text = qs('[data-inventory-text]', inventory);
          if (text) text.textContent = status;
        }
        if (focusMedia && variant.featured_media_id) {
          activateGalleryMedia(variant.featured_media_id);
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };

      choices.forEach((choice) => {
        choice.addEventListener('click', () => {
          if (choice.disabled) return;
          select.value = choice.dataset.variantId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      select.addEventListener('change', () => update({ focusMedia: true }));
      update();

      const sticky = qs('[data-mobile-sticky-atc]', section);
      if (sticky && button) {
        const observer = new IntersectionObserver(([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0 });
        observer.observe(button);
        if (stickyButton) stickyButton.addEventListener('click', () => form.requestSubmit(button));
      }
    });
  }

  function initProductRecommendations() {
    qsa('[data-product-recommendations]').forEach(async (container) => {
      const url = container.dataset.url;
      if (!url) return;
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return;
        const html = document.createElement('div');
        html.innerHTML = await response.text();
        const replacement = qs('[data-product-recommendations]', html);
        if (replacement && replacement.innerHTML.trim()) container.innerHTML = replacement.innerHTML;
      } catch (_) {
        // Recommendations are non-essential; leave the section empty when unavailable.
      }
    });
  }

  function initExitOffer() {
    const modal = qs('[data-exit-modal]');
    if (!modal || sessionStorage.getItem('soierie_exit_seen') === '1') return;
    let enabled = false;
    window.setTimeout(() => { enabled = true; }, 12000);
    const open = (event) => {
      if (!enabled || event.relatedTarget || event.clientY > 5 || overlays.active) return;
      sessionStorage.setItem('soierie_exit_seen', '1');
      overlays.open(modal);
      document.removeEventListener('mouseout', open);
    };
    document.addEventListener('mouseout', open);
    const close = qs('[data-close-exit]', modal);
    if (close) close.addEventListener('click', () => overlays.close());
    const copy = qs('[data-copy-code]', modal);
    if (copy) copy.addEventListener('click', async () => {
      const code = copy.dataset.code || '';
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
        const label = qs('small', copy);
        if (label) label.textContent = t('copied', 'Copied');
      } catch (_) {
        window.prompt(t('copyCode', 'Copy this code:'), code);
      }
    });
  }

  function initPurchaseToasts() {
    const rootElement = qs('[data-purchase-toasts]');
    if (!rootElement) return;
    const templates = qsa('[data-toast-template]', rootElement);
    if (!templates.length) return;
    const interval = Math.max(12000, Number(rootElement.dataset.interval || 24000));
    let index = 0;
    const show = () => {
      if (document.hidden) return;
      const current = qs('.purchase-toast', rootElement);
      if (current) current.remove();
      const fragment = templates[index % templates.length].content.cloneNode(true);
      rootElement.appendChild(fragment);
      const toast = qs('.purchase-toast', rootElement);
      window.setTimeout(() => {
        if (!toast) return;
        toast.classList.add('is-leaving');
        window.setTimeout(() => toast.remove(), 360);
      }, 6000);
      index += 1;
    };
    window.setTimeout(show, Math.min(interval, 9000));
    window.setInterval(show, interval);
  }

  function initSortSelects() {
    qsa('[data-sort-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.assign(url.toString());
      });
    });
  }

  function initLocalizationSelectors() {
    qsa('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });
  }

  function init() {
    initMobileMenu();
    initQuantitySelectors();
    initCountdowns();
    initProductSections();
    initProductRecommendations();
    initExitOffer();
    initPurchaseToasts();
    initSortSelects();
    initLocalizationSelectors();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
